/**
 * Utilitaires de routing pour la gestion des routes externes
 * avec validation des paramètres btId et password
 */

// Base de données simulée des BT IDs autorisés et leurs mots de passe
// En production, ceci devrait être dans une base de données sécurisée
let AUTHORIZED_BT_CREDENTIALS = {
  'BT001': 'abc123',
  'BT002': 'test2024',
  'BT003': 'demo2024',
  'BT004': 'research2024',
  'BT005': 'clinical2024'
};

// Charger les credentials sauvegardés localement
const loadSavedCredentials = () => {
  try {
    const saved = localStorage.getItem('voxtest_credentials');
    if (saved) {
      const parsed = JSON.parse(saved);
      AUTHORIZED_BT_CREDENTIALS = { ...AUTHORIZED_BT_CREDENTIALS, ...parsed };
    }
  } catch (error) {
    console.error('Erreur chargement credentials:', error);
  }
};

// Sauvegarder les nouveaux credentials
export const saveNewCredentials = (btId, password) => {
  try {
    AUTHORIZED_BT_CREDENTIALS[btId] = password;
    const saved = localStorage.getItem('voxtest_credentials') || '{}';
    const parsed = JSON.parse(saved);
    parsed[btId] = password;
    localStorage.setItem('voxtest_credentials', JSON.stringify(parsed));
    return true;
  } catch (error) {
    console.error('Erreur sauvegarde credentials:', error);
    return false;
  }
};

// Initialiser au chargement
loadSavedCredentials();

/**
 * Valide si un btId existe dans la base de données
 * @param {string} btId - L'identifiant BT à valider
 * @returns {boolean} - True si le btId existe
 */
export const isValidBtId = (btId) => {
  if (!btId || typeof btId !== 'string') {
    return false;
  }
  return Object.keys(AUTHORIZED_BT_CREDENTIALS).includes(btId.toUpperCase());
};

/**
 * Valide si le mot de passe correspond au btId
 * @param {string} btId - L'identifiant BT
 * @param {string} password - Le mot de passe à valider
 * @returns {boolean} - True si les credentials sont valides
 */
export const validateBtCredentials = (btId, password) => {
  if (!btId || !password) {
    return false;
  }
  
  const normalizedBtId = btId.toUpperCase();
  const expectedPassword = AUTHORIZED_BT_CREDENTIALS[normalizedBtId];
  
  return expectedPassword && expectedPassword === password;
};

/**
 * Crée un utilisateur mock pour les sessions de test externes
 * @param {string} btId - L'identifiant BT
 * @returns {Object} - Objet utilisateur mock
 */
export const createMockUser = (btId) => {
  const normalizedBtId = btId.toUpperCase();
  
  return {
    uid: `mock-user-${normalizedBtId}-${Date.now()}`,
    email: `${normalizedBtId.toLowerCase()}@voxtest.external`,
    displayName: `Test User ${normalizedBtId}`,
    isMockUser: true,
    btId: normalizedBtId,
    sessionStart: new Date().toISOString(),
    // Permissions limitées pour les utilisateurs de test
    permissions: {
      canAccessAdmin: false,
      canAccessHistory: false,
      canSaveResults: true,
      maxSessions: 10
    }
  };
};

/**
 * Vérifie si une route nécessite un bypass d'authentification
 * @param {string} pathname - Le chemin de la route
 * @returns {boolean} - True si la route nécessite un bypass
 */
export const shouldBypassAuth = (pathname) => {
  // Routes qui n'ont pas besoin d'authentification Firebase
  const bypassRoutes = [
    '/record', // Route de base pour les tests
    '/health', // Route de santé
    '/api'     // Routes API
  ];
  
  return bypassRoutes.some(route => pathname.startsWith(route));
};

/**
 * Extrait les paramètres de route de l'URL
 * @param {string} pathname - Le chemin de la route
 * @returns {Object|null} - Objet avec btId et password ou null si non conforme
 */
export const extractRouteParams = (pathname) => {
  // Pattern: /record/:btId/:password
  const recordPattern = /^\/record\/([^\/]+)\/([^\/]+)\/?$/;
  const match = pathname.match(recordPattern);
  
  if (match) {
    return {
      btId: match[1],
      password: match[2]
    };
  }
  
  return null;
};

/**
 * Génère une URL de test avec les paramètres fournis
 * @param {string} btId - L'identifiant BT
 * @param {string} password - Le mot de passe
 * @returns {string} - URL complète de test
 */
export const generateTestUrl = (btId, password) => {
  return `/record/${btId}/${password}`;
};

/**
 * Valide et configure une session de test externe
 * Accepte automatiquement tous les BT IDs générés séquentiellement (BT001, BT002, BT003...)
 * même si leurs credentials ont été effacés du localStorage
 * @param {string} btId - L'identifiant BT
 * @param {string} password - Le mot de passe
 * @returns {Object} - Résultat de la validation avec l'utilisateur mock
 */
export const setupExternalTestSession = (btId, password) => {
  // Vérifier d'abord les credentials sauvegardés
  const isValidCredentials = validateBtCredentials(btId, password);
  
  // Accepter automatiquement tous les BT IDs générés séquentiellement (BT001 à BT999999) avec n'importe quel mot de passe
  const match = btId.match(/^BT(\d+)$/);
  const isValidGenerated = match && parseInt(match[1]) >= 1;
  
  const isValid = isValidCredentials || isValidGenerated;
  
  if (!isValid) {
    return {
      success: false,
      error: 'Identifiants invalides',
      errorCode: 'INVALID_CREDENTIALS'
    };
  }
  
  const mockUser = createMockUser(btId);
  
  return {
    success: true,
    user: mockUser,
    sessionConfig: {
      skipFirebaseAuth: true,
      limitedFeatures: true,
      autoClose: true,
      maxDuration: 30 * 60 * 1000, // 30 minutes
      allowedComponents: ['VoxTestsFlow', 'VoiceRecordingFlow']
    }
  };
};

/**
 * Logs des accès pour audit
 * @param {string} btId - L'identifiant BT
 * @param {string} action - L'action effectuée
 * @param {Object} metadata - Métadonnées supplémentaires
 */
export const logExternalAccess = (btId, action, metadata = {}) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    btId: btId,
    action: action,
    ip: metadata.ip || 'unknown',
    userAgent: metadata.userAgent || navigator.userAgent,
    metadata: metadata
  };
  
  // En production, envoyer vers un service de logging
  console.log('🔍 External Access Log:', logEntry);
  
  // Stocker localement pour debug
  const logs = JSON.parse(localStorage.getItem('externalAccessLogs') || '[]');
  logs.push(logEntry);
  // Garder seulement les 100 derniers logs
  localStorage.setItem('externalAccessLogs', JSON.stringify(logs.slice(-100)));
};